import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { extractApiError } from '../../lib/api';
import { useAuth } from '../auth/AuthContext';
import { ClientFormModal } from './ClientFormModal';
import { useClientsList, type ClientListFilter } from './api';
import { formatPhone } from './phone';

const PAGE_SIZE = 25;

function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

export function ClientsPage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canMutate = user?.role === 'ADMIN' || user?.role === 'RECEPTION';
  const isAdmin = user?.role === 'ADMIN';

  const [searchInput, setSearchInput] = useState('');
  const search = useDebounced(searchInput.trim(), 300);
  const [includeAnon, setIncludeAnon] = useState(false);
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);

  // Reset paginacji przy zmianie filtrów.
  useEffect(() => {
    setPage(1);
  }, [search, includeAnon]);

  const filter: ClientListFilter = useMemo(
    () => ({
      search: search || undefined,
      page,
      pageSize: PAGE_SIZE,
      includeAnonymized: includeAnon ? 'true' : 'false',
    }),
    [search, page, includeAnon],
  );

  const { data, isLoading, isFetching, error } = useClientsList(filter);
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const items = data?.items ?? [];

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900">{t('clients.title')}</h1>
          <p className="mt-1 text-sm text-zinc-500">{t('clients.subtitle')}</p>
        </div>
        {canMutate && (
          <button type="button" className="btn-primary" onClick={() => setCreateOpen(true)}>
            {t('clients.actions.add')}
          </button>
        )}
      </header>

      <div className="flex flex-wrap items-center gap-3 rounded-xl bg-white p-4 shadow-sm ring-1 ring-zinc-200">
        <input
          type="search"
          placeholder={t('clients.filter.searchPlaceholder')}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="input flex-1 min-w-[14rem]"
        />
        {isAdmin && (
          <label className="flex items-center gap-2 text-sm text-zinc-600">
            <input
              type="checkbox"
              checked={includeAnon}
              onChange={(e) => setIncludeAnon(e.target.checked)}
              className="h-4 w-4 rounded border-zinc-300 text-brand-600 focus:ring-brand-500"
            />
            {t('clients.filter.includeAnonymized')}
          </label>
        )}
        {isFetching && !isLoading && (
          <span className="text-xs text-zinc-400">{t('common.loading')}</span>
        )}
      </div>

      {error && (
        <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
          {extractApiError(error)}
        </div>
      )}

      <div className="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-zinc-200">
        <table className="min-w-full divide-y divide-zinc-200">
          <thead className="bg-zinc-50">
            <tr className="text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
              <th className="px-4 py-3">{t('clients.table.name')}</th>
              <th className="px-4 py-3">{t('clients.table.phone')}</th>
              <th className="px-4 py-3">{t('clients.table.email')}</th>
              <th className="px-4 py-3">{t('clients.table.consents')}</th>
              <th className="px-4 py-3 text-right">{t('clients.table.appointments')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 text-sm">
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-zinc-400">
                  {t('common.loading')}
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-zinc-400">
                  {t('clients.empty')}
                </td>
              </tr>
            ) : (
              items.map((c) => {
                const isAnon = c.anonymizedAt !== null;
                return (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/clients/${c.id}`)}
                    className={`cursor-pointer transition-colors hover:bg-brand-50/60 ${
                      isAnon ? 'bg-zinc-50/60' : ''
                    }`}
                  >
                    <td className="px-4 py-3">
                      {c.mainNote && (
                        <span
                          aria-label={t('clientNote.iconLabel') ?? 'main note'}
                          title={`${t('clientNote.bannerTitle')}: ${c.mainNote.bodyPreview}${c.mainNote.hasMore ? '…' : ''}`}
                          className="mr-1.5 cursor-help text-amber-600"
                        >
                          📌
                        </span>
                      )}
                      <Link
                        to={`/clients/${c.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="font-medium text-zinc-900 hover:text-brand-600"
                      >
                        {c.firstName}
                        {c.lastName ? ` ${c.lastName}` : ''}
                      </Link>
                      {isAnon && (
                        <span className="ml-2 rounded bg-zinc-200 px-1.5 py-0.5 text-xs text-zinc-600">
                          {t('clients.anonymized')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-zinc-700">
                      {isAnon ? '—' : formatPhone(c.phone)}
                    </td>
                    <td className="px-4 py-3 text-zinc-700">{c.email ?? '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {c.rodoConsent && (
                          <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-xs text-emerald-700 ring-1 ring-emerald-200">
                            RODO
                          </span>
                        )}
                        {c.marketingConsent && (
                          <span className="rounded bg-brand-50 px-1.5 py-0.5 text-xs text-brand-700 ring-1 ring-brand-200">
                            {t('clients.marketingShort')}
                          </span>
                        )}
                        {!c.rodoConsent && !c.marketingConsent && (
                          <span className="text-xs text-zinc-400">—</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-zinc-700">
                      {c.appointmentsCount}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-zinc-600">
        <div>
          {t('clients.pagination.summary', {
            from: total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1,
            to: Math.min(total, page * PAGE_SIZE),
            total,
          })}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || isFetching}
            className="rounded-md border border-zinc-300 bg-white px-3 py-1.5 text-zinc-700 transition hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {t('clients.pagination.prev')}
          </button>
          <span className="px-2">
            {page} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || isFetching}
            className="rounded-md border border-zinc-300 bg-white px-3 py-1.5 text-zinc-700 transition hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {t('clients.pagination.next')}
          </button>
        </div>
      </div>

      <ClientFormModal
        open={createOpen}
        client={null}
        onClose={() => setCreateOpen(false)}
      />
    </div>
  );
}
